import { useProjectStore } from '../../store/projectStore';
import { getProjectDetail, getProjectDetailOwner, updateProject } from '../../api/projectApi';
import { ProjectData } from '../../dto/EntityRepository';
import { UpdateProjectReq } from '../../dto/ReqDtoRepository';
import { ProjectResponse } from '../../dto/ResDtoRepository';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';


interface UseProjectDetailStateResult {
  project: ProjectData | null;
  projectId: string | null;
  projectOwnerAui: string | null;
  projectScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseProjectDetailActionsResult {
  getProject: (projectId: string) => Promise<void>;
  getProjectOwner: (projectId: string) => Promise<void>;
  updateProjectHeader: (data: UpdateProjectReq) => Promise<void>;
  updateProjectCoverHeight: (data: UpdateProjectReq) => Promise<void>;
  updateProjectType: (data: UpdateProjectReq) => Promise<void>;
  updateProjectYear: (data: UpdateProjectReq) => Promise<void>;
}

export const useProjectDetailState = (): UseProjectDetailStateResult => {
  const project = useProjectStore((state) => state.project);
  const projectId = useProjectStore((state) => state.projectId);
  const projectOwnerAui = useProjectStore((state) => state.projectOwnerAui);
  const projectScope = useProjectStore((state) => state.projectScope);
  const status = useProjectStore((state) => state.status);
  return { project, projectId, projectOwnerAui, projectScope, status };
};

export const useProjectDetailActions = (): UseProjectDetailActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((state) => state.pageOwner?.aui);
  const beginProjectFetch = useProjectStore((state) => state.beginProjectFetch);
  const setProject = useProjectStore((state) => state.setProject);
  const setProjectError = useProjectStore((state) => state.setProjectError);
  const setProjectHeaderAfterUpdate = useProjectStore((state) => state.setProjectHeaderAfterUpdate);
  const setProjectCoverHeightAfterUpdate = useProjectStore((state) => state.setProjectCoverHeightAfterUpdate);
  const setProjectTypeAfterUpdate = useProjectStore((state) => state.setProjectTypeAfterUpdate);
  const setProjectYearAfterUpdate = useProjectStore((state) => state.setProjectYearAfterUpdate);
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const withApiHandler = useApiWrapper();

  const handleProjectSuccess = (
    response: ProjectResponse,
    ownerAui: string,
    scope: ResourceScope,
    projectId: string,
  ) => {
    const projectData = response.data;
    setProject(projectData, ownerAui, scope, projectId);
  };

  const handleUpdateProjectHeaderSuccess = (response: ProjectResponse) => {
    const projectData = response.data;
    setProjectHeaderAfterUpdate(projectData);
    setUpdatedTempAlert();
  };

  const handleUpdateProjectCoverHeightSuccess = (response: ProjectResponse) => {
    const projectData = response.data;
    setProjectCoverHeightAfterUpdate(projectData);
    setUpdatedTempAlert();
  };

  const handleUpdateProjectTypeSuccess = (response: ProjectResponse) => {
    const projectData = response.data;
    setProjectTypeAfterUpdate(projectData);
    setUpdatedTempAlert();
  };

  const handleUpdateProjectYearSuccess = (response: ProjectResponse) => {
    const projectData = response.data;
    setProjectYearAfterUpdate(projectData);
    setUpdatedTempAlert();
  };

  const fetchProject = async (aui: string, scope: ResourceScope, projectId: string) => {
    beginProjectFetch(aui, scope, projectId);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => (scope === 'owner' ? getProjectDetailOwner(aui, projectId) : getProjectDetail(aui, projectId)),
        (response) => handleProjectSuccess(response, aui, scope, projectId),
        // owner 확정 레이스에서 success/error가 삼켜져 status=loading에 고착되지 않게
        { matchOwnerAccess: false },
      );
    } catch (error) {
      // 이후 owner fetch가 이미 success면 error로 덮지 않음
      const state = useProjectStore.getState();
      if (state.status === 'loading') {
        setProjectError();
      }
      throw error;
    }
  };

  const handleProjectRequest = async (
    action: 'get' | 'get_owner' | 'update_header' | 'update_cover_height' | 'update_type' | 'update_year',
    data?: UpdateProjectReq | string
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'update_header':
          return runCurrentArchiveRequest(
            aui,
            () => updateProject(aui, data as UpdateProjectReq),
            handleUpdateProjectHeaderSuccess,
          );
        case 'update_cover_height':
          return runCurrentArchiveRequest(
            aui,
            () => updateProject(aui, data as UpdateProjectReq),
            handleUpdateProjectCoverHeightSuccess,
          );
        case 'update_type':
          return runCurrentArchiveRequest(
            aui,
            () => updateProject(aui, data as UpdateProjectReq),
            handleUpdateProjectTypeSuccess,
          );
        case 'update_year':
          return runCurrentArchiveRequest(
            aui,
            () => updateProject(aui, data as UpdateProjectReq),
            handleUpdateProjectYearSuccess,
          );
        case 'get_owner':
          return fetchProject(aui, 'owner', data as string);
        case 'get':
        default:
          return fetchProject(aui, 'public', data as string);
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const getProjectHandler = (projectId: string) => handleProjectRequest('get', projectId);
  const getProjectOwnerHandler = (projectId: string) => handleProjectRequest('get_owner', projectId);
  const updateProjectHeaderHandler = (data: UpdateProjectReq) => handleProjectRequest('update_header', data);
  const updateProjectCoverHeightHandler = (data: UpdateProjectReq) => handleProjectRequest('update_cover_height', data);
  const updateProjectTypeHandler = (data: UpdateProjectReq) => handleProjectRequest('update_type', data);
  const updateProjectYearHandler = (data: UpdateProjectReq) => handleProjectRequest('update_year', data);

  return {
    getProject: getProjectHandler,
    getProjectOwner: getProjectOwnerHandler,
    updateProjectHeader: updateProjectHeaderHandler,
    updateProjectCoverHeight: updateProjectCoverHeightHandler,
    updateProjectType: updateProjectTypeHandler,
    updateProjectYear: updateProjectYearHandler
  };
};
